import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shell } from '../components/layout/Shell';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { ScoreRadar, ScoreBars, FitBadge } from '../components/hikari/ScoreBreakdown';
import {
  ArrowRight, CheckCircle, XCircle, CircleNotch, Sparkle,
  Target, ShieldCheck, Clock, PencilSimpleLine, Exam,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const Step = ({ num, title, children }) => (
  <section className="border-t border-border py-14" data-testid={`engine-step-${num}`}>
    <div className="max-w-[1200px] mx-auto px-6 grid lg:grid-cols-12 gap-8">
      <div className="lg:col-span-3">
        <div className="mono text-xs text-muted-foreground mb-1">STEP {String(num).padStart(2, '0')}</div>
        <h2 className="font-display font-black text-3xl lg:text-4xl tracking-tighter">{title}</h2>
      </div>
      <div className="lg:col-span-9">{children}</div>
    </div>
  </section>
);

const Engine = () => {
  const [demo, setDemo] = useState(null);

  // MCQ interaction
  const [mcqAnswer, setMcqAnswer] = useState(null);
  const [mcqResult, setMcqResult] = useState(null);
  const [mcqGrading, setMcqGrading] = useState(false);

  // Writing interaction
  const [writing, setWriting] = useState('');
  const [writingResult, setWritingResult] = useState(null);
  const [writingGrading, setWritingGrading] = useState(false);

  useEffect(() => {
    api.get('/public/engine-demo').then((r) => setDemo(r.data));
  }, []);

  const submitMcq = async () => {
    if (mcqAnswer === null) return;
    setMcqGrading(true);
    try {
      const { data } = await api.post('/public/grade-mcq', {
        item_id: demo.mcq.id,
        selected_index: mcqAnswer,
      });
      setMcqResult(data);
    } catch (e) {
      toast.error('Grade failed');
    } finally { setMcqGrading(false); }
  };

  const submitWriting = async () => {
    if (writing.trim().length < 40) {
      toast.error('Write at least a few sentences (40+ characters).');
      return;
    }
    setWritingGrading(true);
    try {
      const { data } = await api.post('/public/grade-writing', {
        item_id: demo.writing.id,
        text: writing,
      });
      setWritingResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Grading failed');
    } finally { setWritingGrading(false); }
  };

  if (!demo) return <Shell><div className="p-8 mono text-sm">Loading engine...</div></Shell>;

  const wordCount = writing.trim() ? writing.trim().split(/\s+/).length : 0;

  return (
    <Shell>
      {/* Hero */}
      <section className="border-b border-border" data-testid="engine-hero">
        <div className="max-w-[1200px] mx-auto px-6 py-20">
          <div className="label-eyebrow text-primary mb-4">THE ENGINE · LIVE</div>
          <h1 className="font-display font-black text-5xl lg:text-6xl tracking-tighter max-w-4xl mb-6">
            Answer a real question. Watch the score build.
          </h1>
          <p className="text-muted-foreground max-w-2xl leading-relaxed">
            This is the actual Hikari scoring engine — not a mockup. You'll answer a real MCQ,
            write a short client email, and see both graded in real time (writing goes through
            Claude Sonnet 4.5 against the job rubric). Then we'll show how four independent
            sub-scores aggregate into one transparent fit score.
          </p>
        </div>
      </section>

      {/* STEP 1 — MCQ */}
      <Step num={1} title="Auto-graded MCQ">
        <div className="border border-border bg-white p-6 mb-6">
          <div className="flex items-center gap-2 label-eyebrow text-primary mb-3">
            <Exam size={16} /> ADMIN & OPS · JOB-RELEVANT
          </div>
          <p className="font-medium mb-5">{demo.mcq.prompt}</p>
          <div className="space-y-2">
            {demo.mcq.options.map((opt, i) => {
              const isPicked = mcqAnswer === i;
              const isCorrect = mcqResult && i === mcqResult.correct_index;
              const isWrong = mcqResult && mcqAnswer === i && !mcqResult.correct;
              return (
                <button
                  key={i}
                  onClick={() => !mcqResult && setMcqAnswer(i)}
                  disabled={!!mcqResult}
                  className={`w-full text-left p-3 border transition-colors ${
                    isCorrect ? 'bg-primary text-primary-foreground border-primary' :
                    isWrong ? 'bg-destructive text-destructive-foreground border-destructive' :
                    isPicked ? 'bg-foreground text-background border-foreground' :
                    'border-border bg-white hover:bg-secondary/40'
                  }`}
                  data-testid={`engine-mcq-opt-${i}`}
                >
                  <span className="mono text-xs mr-3 opacity-70">{String.fromCharCode(65 + i)}</span>
                  {opt}
                  {isCorrect && <CheckCircle size={16} weight="fill" className="inline ml-2" />}
                </button>
              );
            })}
          </div>

          {!mcqResult && (
            <Button onClick={submitMcq} disabled={mcqAnswer === null || mcqGrading} className="rounded-none mt-5" data-testid="engine-mcq-submit">
              {mcqGrading ? 'Grading...' : 'Grade my answer'}
            </Button>
          )}

          {mcqResult && (
            <div className="mt-5 border-t border-border pt-4" data-testid="engine-mcq-result">
              <div className="flex items-center gap-3 mb-2">
                {mcqResult.correct
                  ? <CheckCircle size={22} weight="fill" className="text-primary" />
                  : <XCircle size={22} weight="fill" className="text-destructive" />
                }
                <span className="font-display font-black text-xl tracking-tight">
                  {mcqResult.correct ? 'Correct — 100/100' : 'Not quite — 0/100'}
                </span>
              </div>
              <p className="text-sm leading-relaxed text-muted-foreground">{mcqResult.explanation}</p>
            </div>
          )}
        </div>
        <div className="mono text-xs text-muted-foreground">
          MCQ scores are instant and deterministic — no LLM needed. Each item tests job-relevant judgment, not trivia.
        </div>
      </Step>

      {/* STEP 2 — Writing with LLM */}
      <Step num={2} title="AI-graded writing">
        <div className="border border-border bg-white p-6 mb-6">
          <div className="flex items-center gap-2 label-eyebrow text-primary mb-3">
            <PencilSimpleLine size={16} /> CLIENT FOLLOW-UP · CLAUDE SONNET 4.5
          </div>
          <p className="font-medium mb-4">{demo.writing.prompt}</p>
          <div className="p-3 bg-secondary/60 text-xs label-eyebrow text-muted-foreground mb-3">
            RUBRIC: <span className="normal-case tracking-normal font-normal">{demo.writing.rubric}</span>
          </div>
          <Textarea
            rows={8}
            value={writing}
            onChange={(e) => setWriting(e.target.value)}
            disabled={!!writingResult}
            className="rounded-none"
            placeholder="Draft your follow-up email here..."
            data-testid="engine-writing-input"
          />
          <div className="flex items-center justify-between mt-2 mono text-xs text-muted-foreground">
            <span>Words: {wordCount}</span>
            <span>Min: {demo.writing.min_words}</span>
          </div>

          {!writingResult && (
            <Button onClick={submitWriting} disabled={writingGrading || writing.trim().length < 40} className="rounded-none mt-4" data-testid="engine-writing-submit">
              {writingGrading ? (<><CircleNotch className="animate-spin mr-2" size={16} /> Grading with Claude…</>) : 'Grade with Claude Sonnet 4.5'}
            </Button>
          )}

          {writingResult && (
            <div className="mt-5 border-t border-border pt-4" data-testid="engine-writing-result">
              <div className="flex items-end gap-4 mb-3">
                <FitBadge value={writingResult.score} size="md" />
                <div>
                  <div className="label-eyebrow text-muted-foreground">AI RUBRIC SCORE</div>
                  <div className="mono font-bold text-3xl">{writingResult.score}/100</div>
                </div>
              </div>
              <div className="label-eyebrow text-muted-foreground mb-2">FEEDBACK</div>
              <p className="text-sm leading-relaxed italic">"{writingResult.feedback}"</p>
            </div>
          )}
        </div>
        <div className="mono text-xs text-muted-foreground">
          The LLM never sees the candidate's identity. Only prompt + rubric + answer. Same grading applied to every candidate, at scale.
        </div>
      </Step>

      {/* STEP 3 — Layered scoring */}
      <Step num={3} title="Four independent signals → one fit score">
        <div className="grid md:grid-cols-4 gap-0 grid-skeleton mb-6" data-testid="engine-layer-cards">
          {[
            { icon: Target, title: 'Performance', body: 'How well did they do on the assessments, graded strictly to the rubric.' },
            { icon: Clock, title: 'Consistency', body: 'Variance across attempts. Luck fades; skill repeats. Stable = signal.' },
            { icon: ShieldCheck, title: 'Reliability', body: 'Anti-gaming: abnormal speed, duplicate answers, LLM-generated detection.' },
            { icon: Sparkle, title: 'Artifacts', body: 'Real work samples + verified credentials, weighted against the job\'s skills.' },
          ].map((p, i) => (
            <div key={i} className="p-5 bg-white">
              <p.icon size={24} weight="duotone" className="text-primary mb-3" />
              <div className="font-display font-black text-lg tracking-tight mb-1">{p.title}</div>
              <p className="text-xs text-muted-foreground leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
        <div className="mono text-xs text-muted-foreground leading-relaxed">
          overall_fit = <span className="font-bold">w1·performance + w2·consistency + w3·reliability + w4·artifacts</span>
          <br />Weights are set per role family — an Ops role weights consistency higher; a Customer Support role weights performance higher.
        </div>
      </Step>

      {/* STEP 4 — Real candidate */}
      <Step num={4} title="What the employer actually sees">
        <div className="border border-border bg-white p-6" data-testid="engine-sample-candidate">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
            <div>
              <div className="label-eyebrow text-muted-foreground">CANDIDATE FOR</div>
              <h3 className="font-display font-black text-2xl tracking-tight">{demo.sample_score.candidate_name}</h3>
              <div className="mono text-sm text-muted-foreground mt-1">{demo.sample_score.role}</div>
            </div>
            <FitBadge value={demo.sample_score.overall_fit} size="lg" />
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-6">
            <div>
              <div className="label-eyebrow text-muted-foreground mb-3">SCORE RADAR</div>
              <ScoreRadar scores={demo.sample_score} />
            </div>
            <div>
              <div className="label-eyebrow text-muted-foreground mb-3">SUB-SCORE BREAKDOWN</div>
              <ScoreBars scores={demo.sample_score} />
            </div>
          </div>

          <div className="border-t border-border pt-4 mb-4">
            <div className="label-eyebrow text-muted-foreground mb-1">AI EXPLANATION</div>
            <p className="text-sm leading-relaxed">{demo.sample_score.explanation}</p>
          </div>
          <div>
            <div className="label-eyebrow text-muted-foreground mb-1">WORK-STYLE PROFILE</div>
            <p className="text-sm leading-relaxed italic">"{demo.sample_score.work_style}"</p>
          </div>
        </div>
      </Step>

      {/* CTA */}
      <section className="bg-foreground text-background">
        <div className="max-w-[1200px] mx-auto px-6 py-20 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="label-eyebrow text-accent mb-3">READY TO BUILD YOURS?</div>
            <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">Every candidate gets the same engine.</h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/register?role=candidate">
              <Button size="lg" className="rounded-none h-12 px-6 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="engine-cta-candidate">
                Take an assessment <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
            <Link to="/register?role=employer">
              <Button size="lg" variant="outline" className="rounded-none h-12 px-6 bg-transparent border-background text-background hover:bg-background hover:text-foreground" data-testid="engine-cta-employer">
                Post a job <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Shell>
  );
};

export default Engine;
